"use client";

import { useEffect, useState } from "react";

import { PreBidForm } from "@/components/app/profile/pre-bid-form";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTokenOrderbook } from "@/hooks/use-token-orderbook";
import { useTotalSupply } from "@/hooks/use-total-supply";

import { CurrentBids } from "../../../profile/current-bids";
import { PreLaunchMetrics } from "../../../profile/token-pre-launch-overview";
import { HoldersTab } from "../holders-tab/holders-tab";
import { TokenDetailsHeader } from "../token-details-header";
import type { useTokenMetrics } from "../use-token-metrics";

export const UpcomingTokenDetails = ({
  metrics,
}: {
  metrics: ReturnType<typeof useTokenMetrics>;
}) => {
  const [activeTab, setActiveTab] = useState<string>("overview");
  const { totalSupply, loadingTotalSupply } = useTotalSupply({
    publicKey: metrics?.selectedPublicKey ?? "",
  });

  useEffect(() => {
    setActiveTab("overview");
  }, [metrics?.selectedPublicKey]);

  const {
    selectedPublicKey,
    tokenDetails,
    totalBidsFocus,
    totalBids,
    focusExchangeRate,
    coinUserName,
    specificCoinLoading,
    uniqueWallets,
    username,
  } = metrics;

  const { tokenOrderbook, loading: loadingTokenOrderbook } = useTokenOrderbook({
    baseCurrencyPublicKeyBase58Check:
      tokenDetails?.coinProperties?.AmmConfigs?.[0]?.BaseCurrencyPkid ??
      metrics?.selectedPublicKey ??
      "",
    quoteCurrencyPublicKeyBase58Check:
      tokenDetails?.coinProperties.AmmConfigs?.[0]?.QuoteCurrencyPkid ??
      process.env.NEXT_PUBLIC_FOCUS_APP_PUBLIC_KEY,
  });

  return (
    <div
      className="
        relative hidden w-5/12 bg-accent p-6
        sm:block
      "
    >
      {selectedPublicKey && (
        <>
          {specificCoinLoading || loadingTotalSupply ? (
            <div className="sticky top-[10px] flex flex-col gap-4">
              <Skeleton className="h-8 w-32" />
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          ) : (
            <div className="sticky top-[10px]">
              <div
                className="
                custom-scrollbar h-full max-h-screen overflow-y-auto
              "
              >
                <TokenDetailsHeader
                  {...{
                    username,
                    publicKey: selectedPublicKey,
                    extraData: tokenDetails?.coinProperties.Profile.ExtraData,
                  }}
                />

                <Tabs
                  value={activeTab}
                  onValueChange={setActiveTab}
                  className="mt-4"
                >
                  <TabsList
                    className="
                      m-auto mb-4 flex min-w-fit items-center justify-center
                      gap-1 rounded-full border border-transparent bg-card p-1
                      text-center text-muted-foreground
                    "
                  >
                    <TabsTrigger
                      value="overview"
                      className="
                        flex gap-2 rounded-full text-xs
                        data-active:bg-background
                        data-active:text-muted-foreground
                        sm:text-sm
                      "
                    >
                      Overview
                    </TabsTrigger>
                    <TabsTrigger
                      value="holders"
                      className="
                        flex gap-2 rounded-full text-xs
                        data-active:bg-background
                        data-active:text-muted-foreground
                        sm:text-sm
                      "
                    >
                      Holders
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="overview">
                    <div className="flex flex-col gap-2">
                      {tokenDetails && (
                        <CurrentBids
                          orders={[...(tokenOrderbook?.bids || [])]}
                          loading={loadingTokenOrderbook}
                          coinUsername={coinUserName ?? ""}
                          totalSupply={totalSupply}
                          className="max-h-[400px]"
                          tokenPublicKey={selectedPublicKey}
                        />
                      )}
                    </div>
                    <div className="mt-2 grid w-full grid-cols-1 gap-4">
                      <PreLaunchMetrics
                        wrapperClassName="mt-2 flex-col md:flex-row"
                        {...{
                          totalBidsFocus,
                          focusExchangeRate,
                          totalBids,
                          uniqueWallets,
                        }}
                      />

                      <PreBidForm publicKey={selectedPublicKey} />
                    </div>
                  </TabsContent>

                  <TabsContent value="holders">
                    <HoldersTab publicKey={selectedPublicKey} />
                  </TabsContent>
                </Tabs>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
